/* ==========================================================================
   IT Factory Australia Custom Header
   File: assets/itf-header.js
   Scope: Custom ITF header only
   ========================================================================== */

(function () {
  'use strict';

  const SELECTORS = {
    header: '[data-itf-header]',
    navItem: '[data-itf-nav-item]',
    navTrigger: '[data-itf-nav-trigger]',
    navPanel: '[data-itf-nav-panel]',
    drawer: '[data-itf-drawer]',
    drawerOpen: '[data-itf-drawer-open]',
    drawerClose: '[data-itf-drawer-close]',
    drawerToggle: '[data-itf-drawer-toggle]',
    drawerSubmenu: '[data-itf-drawer-submenu]',
    overlay: '[data-itf-overlay]',
    mobileSearch: '[data-itf-mobile-search]',
    mobileSearchOpen: '[data-itf-mobile-search-open]',
    mobileSearchClose: '[data-itf-mobile-search-close]',
    searchInput: '[data-itf-search-input]',
    searchReset: '[data-itf-search-reset]',
    cartTrigger: '[data-itf-cart-trigger]',
    dropdownItemHasChildren: '.itf-header__dropdown-item--has-children',
    stickyArea: '[data-itf-sticky-area]',
    stickySpacer: '[data-itf-sticky-spacer]'
  };

  const CLASSES = {
    open: 'is-open',
    sticky: 'is-sticky',
    hidden: 'is-hidden',
    visible: 'is-visible',
    fixed: 'is-fixed',
    active: 'is-active',
    drawerBodyOpen: 'itf-header-drawer-open',
    menuBodyOpen: 'itf-header-menu-open'
  };

  const BREAKPOINT = 991;

  class ITFHeader {
    constructor(header) {
      this.header = header;
      this.desktopMedia = window.matchMedia(`(min-width: ${BREAKPOINT + 1}px)`);

      this.stickyArea = this.header.querySelector(SELECTORS.stickyArea) || this.header;
      this.stickySpacer = this.header.querySelector(SELECTORS.stickySpacer);
      this.drawer = this.header.querySelector(SELECTORS.drawer);
      this.overlay = this.header.querySelector(SELECTORS.overlay);
      this.mobileSearch = this.header.querySelector(SELECTORS.mobileSearch);

      this.navItems = Array.from(this.header.querySelectorAll(SELECTORS.navItem));
      this.drawerOpenButtons = Array.from(this.header.querySelectorAll(SELECTORS.drawerOpen));
      this.mobileSearchOpenButtons = Array.from(this.header.querySelectorAll(SELECTORS.mobileSearchOpen));
      this.drawerToggles = Array.from(this.header.querySelectorAll(SELECTORS.drawerToggle));
      this.searchInputs = Array.from(this.header.querySelectorAll(SELECTORS.searchInput));
      this.searchResetButtons = Array.from(this.header.querySelectorAll(SELECTORS.searchReset));
      this.cartTriggers = Array.from(this.header.querySelectorAll(SELECTORS.cartTrigger));
      this.dropdownFlyoutItems = Array.from(this.header.querySelectorAll(SELECTORS.dropdownItemHasChildren));

      this.hoverCloseTimers = new WeakMap();
      this.previousFocus = null;
      this.lastScrollY = window.scrollY || 0;
      this.ticking = false;
      this.suppressedHoverItem = null;
      this.stickyStart = 0;
      this.stickyHeight = 0;
      this.resizeTimer = null;

      this.settings = {
        stickyEnabled: this.stickyArea.dataset.sticky === 'true',
        stickyMode: this.stickyArea.dataset.stickyMode || 'always',
        menuTrigger: this.header.dataset.desktopTrigger || 'hover_and_focus',
        overlayEnabled: this.header.dataset.overlay === 'true',
        escapeCloseEnabled: this.header.dataset.escapeClose !== 'false',
        clickOutsideEnabled: this.header.dataset.clickOutside !== 'false',
        closeDelay: Number(this.header.dataset.dropdownCloseDelay || 120)
      };

      this.init();
    }

    init() {
      this.bindDesktopMenus();
      this.bindDropdownFlyouts();
      this.bindDrawer();
      this.bindMobileSearch();
      this.bindSearchReset();
      this.bindCartDrawer();
      this.bindGlobalEvents();
      this.bindStickyHeader();
      this.handleResponsiveState();

      if (this.desktopMedia.addEventListener) {
        this.desktopMedia.addEventListener('change', () => this.handleResponsiveState());
      } else if (this.desktopMedia.addListener) {
        this.desktopMedia.addListener(() => this.handleResponsiveState());
      }
    }

    /* --------------------------------------------------------------------------
       Desktop dropdown / mega menu
       -------------------------------------------------------------------------- */

    bindDesktopMenus() {
      this.navItems.forEach((item) => {
        const trigger = item.querySelector(SELECTORS.navTrigger);
        const panel = item.querySelector(SELECTORS.navPanel);

        if (!trigger || !panel) return;

        trigger.setAttribute('aria-expanded', 'false');

        if (!panel.id) {
          panel.id = `itf-header-panel-${Math.random().toString(36).slice(2, 10)}`;
        }

        trigger.setAttribute('aria-controls', panel.id);

        item.addEventListener('mouseenter', () => {
          if (!this.isDesktop()) return;
          if (this.settings.menuTrigger !== 'hover_and_focus') return;
          if (this.suppressedHoverItem === item) return;

          this.cancelCloseTimer(item);
          this.openMenuItem(item);
        });

        item.addEventListener('mouseleave', (event) => {
          if (!this.isDesktop()) return;
          if (this.settings.menuTrigger !== 'hover_and_focus') return;

          if (event.relatedTarget && item.contains(event.relatedTarget)) return;

          if (this.suppressedHoverItem === item) {
            this.suppressedHoverItem = null;
          }

          this.queueCloseMenuItem(item);
        });

        item.addEventListener('focusin', () => {
          if (!this.isDesktop()) return;
          if (this.settings.menuTrigger !== 'hover_and_focus') return;

          this.cancelCloseTimer(item);
          this.openMenuItem(item);
        });

        item.addEventListener('focusout', () => {
          if (!this.isDesktop()) return;

          window.setTimeout(() => {
            if (!item.contains(document.activeElement)) {
              this.closeMenuItem(item);
            }
          }, 0);
        });

        trigger.addEventListener('click', (event) => {
          if (!this.isDesktop()) return;

          const isOpen = item.classList.contains(CLASSES.open);
          const parentBehavior = trigger.dataset.itfParentBehavior || '';

          const shouldToggle =
            this.settings.menuTrigger === 'click' ||
            parentBehavior === 'open_only' ||
            isOpen;

          if (!shouldToggle) return;

          event.preventDefault();

          if (isOpen) {
            this.suppressedHoverItem = item;
            this.closeMenuItem(item);
            trigger.blur();
          } else {
            this.closeAllMenuItems(item);
            this.openMenuItem(item);
          }
        });

        panel.addEventListener('mouseenter', () => {
          if (!this.isDesktop()) return;
          this.cancelCloseTimer(item);
        });

        panel.addEventListener('mouseleave', (event) => {
          if (!this.isDesktop()) return;
          if (event.relatedTarget && item.contains(event.relatedTarget)) return;
          this.queueCloseMenuItem(item);
        });
      });
    }

    bindDropdownFlyouts() {
      if (!this.dropdownFlyoutItems.length) return;

      this.dropdownFlyoutItems.forEach((item) => {
        const updateDirection = () => {
          const sublist = item.querySelector('.itf-header__dropdown-sublist');
          if (!sublist) return;

          item.classList.remove('itf-header__dropdown-item--flyout-left');

          const itemRect = item.getBoundingClientRect();
          const estimatedWidth = Math.max(sublist.offsetWidth || 260, 230);
          const viewportPadding = 18;
          const wouldOverflowRight = itemRect.right + estimatedWidth > window.innerWidth - viewportPadding;

          if (wouldOverflowRight) {
            item.classList.add('itf-header__dropdown-item--flyout-left');
          }
        };

        item.addEventListener('mouseenter', updateDirection);
        item.addEventListener('focusin', updateDirection);
      });
    }

    openMenuItem(item) {
      if (!item) return;

      const trigger = item.querySelector(SELECTORS.navTrigger);

      this.closeAllMenuItems(item);
      item.classList.add(CLASSES.open);

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      document.body.classList.add(CLASSES.menuBodyOpen);
    }

    closeMenuItem(item) {
      if (!item) return;

      const trigger = item.querySelector(SELECTORS.navTrigger);

      item.classList.remove(CLASSES.open);

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }

      if (!this.hasOpenMenuItems()) {
        document.body.classList.remove(CLASSES.menuBodyOpen);
      }
    }

    closeAllMenuItems(exceptItem = null) {
      this.navItems.forEach((item) => {
        if (exceptItem && item === exceptItem) return;
        this.closeMenuItem(item);
      });
    }

    hasOpenMenuItems() {
      return this.navItems.some((item) => item.classList.contains(CLASSES.open));
    }

    queueCloseMenuItem(item) {
      this.cancelCloseTimer(item);

      const timer = window.setTimeout(() => {
        this.closeMenuItem(item);
      }, this.settings.closeDelay);

      this.hoverCloseTimers.set(item, timer);
    }

    cancelCloseTimer(item) {
      const timer = this.hoverCloseTimers.get(item);

      if (timer) {
        window.clearTimeout(timer);
        this.hoverCloseTimers.delete(item);
      }
    }

    /* --------------------------------------------------------------------------
       Mobile / tablet drawer
       -------------------------------------------------------------------------- */

    bindDrawer() {
      const openButtons = Array.from(this.header.querySelectorAll(SELECTORS.drawerOpen));
      const closeButtons = Array.from(this.header.querySelectorAll(SELECTORS.drawerClose));

      openButtons.forEach((button) => {
        button.addEventListener('click', () => this.openDrawer(button));
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.closeDrawer();
          this.closeAllMenuItems();
          this.closeMobileSearch();
        });
      }

      this.drawerToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');

        const submenu = this.getDrawerSubmenu(toggle);

        if (submenu) {
          if (!submenu.id) {
            submenu.id = `itf-header-drawer-submenu-${Math.random().toString(36).slice(2, 10)}`;
          }

          toggle.setAttribute('aria-controls', submenu.id);
        }

        toggle.addEventListener('click', () => {
          this.toggleDrawerSubmenu(toggle);
        });
      });
    }

    openDrawer(trigger = null) {
      if (!this.drawer) return;

      this.previousFocus = trigger || document.activeElement;

      this.drawer.classList.add(CLASSES.open);
      this.drawer.setAttribute('aria-hidden', 'false');

      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      document.body.classList.add(CLASSES.drawerBodyOpen);

      this.openOverlay();

      const firstFocusable = this.getFocusableElements(this.drawer)[0];
      if (firstFocusable) {
        window.setTimeout(() => firstFocusable.focus(), 80);
      }
    }

    closeDrawer() {
      if (!this.drawer) return;

      this.drawer.classList.remove(CLASSES.open);
      this.drawer.setAttribute('aria-hidden', 'true');

      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      document.body.classList.remove(CLASSES.drawerBodyOpen);

      this.closeOverlay();

      this.drawerToggles.forEach((toggle) => {
        const item = toggle.closest('.itf-header__drawer-subitem, .itf-header__drawer-item');

        if (item) {
          item.classList.remove(CLASSES.open);
        }

        toggle.setAttribute('aria-expanded', 'false');
      });

      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        window.setTimeout(() => this.previousFocus.focus(), 80);
      }

      this.previousFocus = null;
    }

    toggleDrawerSubmenu(toggle) {
      const item = toggle.closest('.itf-header__drawer-item, .itf-header__drawer-subitem');
      if (!item) return;

      const isOpen = item.classList.contains(CLASSES.open);

      item.classList.toggle(CLASSES.open, !isOpen);
      toggle.setAttribute('aria-expanded', String(!isOpen));
    }

    getDrawerSubmenu(toggle) {
      const item = toggle.closest('.itf-header__drawer-subitem, .itf-header__drawer-item');
      if (!item) return null;

      const directSubmenus = Array.from(item.children).filter((child) =>
        child.matches && child.matches('[data-itf-drawer-submenu]')
      );

      return directSubmenus[0] || null;
    }

    /* --------------------------------------------------------------------------
       Mobile search
       -------------------------------------------------------------------------- */

    bindMobileSearch() {
      const openButtons = Array.from(this.header.querySelectorAll(SELECTORS.mobileSearchOpen));
      const closeButtons = Array.from(this.header.querySelectorAll(SELECTORS.mobileSearchClose));

      openButtons.forEach((button) => {
        button.addEventListener('click', () => this.toggleMobileSearch());
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeMobileSearch());
      });
    }

    toggleMobileSearch() {
      if (!this.mobileSearch) return;

      if (this.mobileSearch.classList.contains(CLASSES.open)) {
        this.closeMobileSearch();
      } else {
        this.openMobileSearch();
      }
    }

    openMobileSearch() {
      if (!this.mobileSearch) return;

      this.mobileSearch.classList.add(CLASSES.open);
      this.mobileSearch.setAttribute('aria-hidden', 'false');

      this.mobileSearchOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      const input = this.mobileSearch.querySelector(SELECTORS.searchInput);

      if (input) {
        window.setTimeout(() => input.focus(), 80);
      }
    }

    closeMobileSearch() {
      if (!this.mobileSearch) return;

      this.mobileSearch.classList.remove(CLASSES.open);
      this.mobileSearch.setAttribute('aria-hidden', 'true');

      this.mobileSearchOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
    }

    /* --------------------------------------------------------------------------
       Search reset + syncing
       -------------------------------------------------------------------------- */

    bindSearchReset() {
      this.searchInputs.forEach((input) => {
        this.updateSearchReset(input);

        input.addEventListener('input', () => {
          this.updateSearchReset(input);
          this.syncSearchInputs(input);
        });
      });

      this.searchResetButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();

          const form = button.closest('form');
          const input = form ? form.querySelector(SELECTORS.searchInput) : null;

          if (!input) return;

          input.value = '';
          input.focus();
          this.updateSearchReset(input);
          this.syncSearchInputs(input);
        });
      });
    }

    /* --------------------------------------------------------------------------
       Cart drawer trigger
       -------------------------------------------------------------------------- */

    bindCartDrawer() {
      if (!this.cartTriggers || !this.cartTriggers.length) return;

      this.cartTriggers.forEach((trigger) => {
        if (trigger.dataset.itfCartBound === 'true') return;
        trigger.dataset.itfCartBound = 'true';

        const handleOpen = (event) => {
          const cartDrawer = this.getCartDrawer();

          /*
            If Refresh has not rendered a cart drawer, do not intercept.
            The link will behave normally and go to the cart page.
          */
          if (!cartDrawer) return;

          event.preventDefault();

          this.closeAllMenuItems();
          this.closeMobileSearch();

          if (this.isDrawerOpen()) {
            this.closeDrawer();
          }

          this.openCartDrawer(trigger);
        };

        trigger.addEventListener('click', handleOpen, true);

        trigger.addEventListener('keydown', (event) => {
          if (event.key !== ' ' && event.key !== 'Enter') return;
          handleOpen(event);
        });
      });
    }

    getCartDrawer() {
      return document.querySelector('cart-drawer');
    }

    openCartDrawer(trigger) {
      const cartDrawer = this.getCartDrawer();

      if (!cartDrawer) {
        window.location.href = trigger.href;
        return;
      }

      this.closeAllMenuItems();
      this.closeMobileSearch();

      if (this.isDrawerOpen()) {
        this.closeDrawer();
      }

      /*
        Use Refresh's native drawer method first when available.
      */
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open(trigger);

        window.setTimeout(() => {
          cartDrawer.classList.add('animate', 'active');
          cartDrawer.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }, 20);

        return;
      }

      /*
        Manual fallback for Refresh-style cart drawer.
      */
      if (typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(trigger);
      }

      cartDrawer.classList.add('animate');

      window.requestAnimationFrame(() => {
        cartDrawer.classList.add('active');
        cartDrawer.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');

        const drawerDialog =
          cartDrawer.querySelector('[role="dialog"]') ||
          cartDrawer.querySelector('#CartDrawer') ||
          cartDrawer.querySelector('.drawer__inner');

        const focusTarget =
          cartDrawer.querySelector('.drawer__close') ||
          drawerDialog;

        if (typeof trapFocus === 'function' && drawerDialog && focusTarget) {
          trapFocus(drawerDialog, focusTarget);
        } else if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }
      });
    }

    updateSearchReset(input) {
      if (!input) return;

      const form = input.closest('form');
      if (!form) return;

      const reset = form.querySelector(SELECTORS.searchReset);
      if (!reset) return;

      reset.classList.toggle(CLASSES.visible, input.value.trim().length > 0);
    }

    syncSearchInputs(sourceInput) {
      this.searchInputs.forEach((input) => {
        if (input === sourceInput) return;
        input.value = sourceInput.value;
        this.updateSearchReset(input);
      });
    }

    /* --------------------------------------------------------------------------
       Sticky header
       -------------------------------------------------------------------------- */

    bindStickyHeader() {
      if (!this.settings.stickyEnabled) return;
      if (this.settings.stickyMode === 'none') return;
      if (!this.stickyArea) return;

      this.calculateStickyMetrics();
      this.handleStickyScroll();

      window.addEventListener(
        'scroll',
        () => {
          if (!this.ticking) {
            window.requestAnimationFrame(() => {
              this.handleStickyScroll();
              this.ticking = false;
            });

            this.ticking = true;
          }
        },
        { passive: true }
      );

      window.addEventListener(
        'resize',
        () => {
          window.clearTimeout(this.resizeTimer);

          this.resizeTimer = window.setTimeout(() => {
            this.resetStickyState();
            this.calculateStickyMetrics();
            this.handleStickyScroll();
          }, 150);
        },
        { passive: true }
      );

      window.addEventListener('load', () => {
        this.resetStickyState();
        this.calculateStickyMetrics();
        this.handleStickyScroll();
      });
    }

    handleStickyScroll() {
      if (!this.settings.stickyEnabled || this.settings.stickyMode === 'none') return;

      const currentScrollY = window.scrollY || 0;
      const isScrollingDown = currentScrollY > this.lastScrollY;
      const isPastStickyStart = currentScrollY >= this.stickyStart;

      if (isPastStickyStart) {
        this.stickyArea.classList.add(CLASSES.fixed, CLASSES.sticky);

        if (this.stickySpacer) {
          this.stickySpacer.classList.add(CLASSES.active);
          this.stickySpacer.style.height = `${this.stickyHeight}px`;
        }
      } else {
        this.stickyArea.classList.remove(CLASSES.fixed, CLASSES.sticky, CLASSES.hidden);

        if (this.stickySpacer) {
          this.stickySpacer.classList.remove(CLASSES.active);
          this.stickySpacer.style.height = '0px';
        }

        this.lastScrollY = currentScrollY;
        return;
      }

      if (this.settings.stickyMode === 'on_scroll_up') {
        if (isScrollingDown && !this.hasOpenMenuItems() && !this.isDrawerOpen()) {
          this.stickyArea.classList.add(CLASSES.hidden);
        } else {
          this.stickyArea.classList.remove(CLASSES.hidden);
        }
      } else {
        this.stickyArea.classList.remove(CLASSES.hidden);
      }

      this.lastScrollY = currentScrollY;
    }

    calculateStickyMetrics() {
      if (!this.stickyArea) return;

      const wasFixed = this.stickyArea.classList.contains(CLASSES.fixed);

      if (wasFixed) {
        this.stickyArea.classList.remove(CLASSES.fixed, CLASSES.sticky, CLASSES.hidden);
      }

      if (this.stickySpacer) {
        this.stickySpacer.classList.remove(CLASSES.active);
        this.stickySpacer.style.height = '0px';
      }

      const rect = this.stickyArea.getBoundingClientRect();

      this.stickyStart = rect.top + (window.scrollY || 0);
      this.stickyHeight = this.stickyArea.offsetHeight || rect.height || 0;
    }

    resetStickyState() {
      if (!this.stickyArea) return;

      this.stickyArea.classList.remove(CLASSES.fixed, CLASSES.sticky, CLASSES.hidden);

      if (this.stickySpacer) {
        this.stickySpacer.classList.remove(CLASSES.active);
        this.stickySpacer.style.height = '0px';
      }
    }

    /* --------------------------------------------------------------------------
       Global interactions
       -------------------------------------------------------------------------- */

    bindGlobalEvents() {
      document.addEventListener('keydown', (event) => {
        if (!this.settings.escapeCloseEnabled) return;
        if (event.key !== 'Escape') return;

        this.closeAllMenuItems();
        this.closeMobileSearch();

        if (this.isDrawerOpen()) {
          this.closeDrawer();
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.settings.clickOutsideEnabled) return;

        const clickedInsideHeader = this.header.contains(event.target);

        if (!clickedInsideHeader) {
          this.closeAllMenuItems();
          this.closeMobileSearch();

          if (this.isDrawerOpen()) {
            this.closeDrawer();
          }
        }
      });

      document.addEventListener('focusin', (event) => {
        if (this.header.contains(event.target)) return;

        this.closeAllMenuItems();
      });

      this.header.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        if (!this.isDrawerOpen()) return;

        this.trapDrawerFocus(event);
      });
    }

    handleResponsiveState() {
      if (this.isDesktop()) {
        this.closeDrawer();
        this.closeMobileSearch();
      } else {
        this.closeAllMenuItems();
      }
    }

    openOverlay() {
      if (!this.overlay || !this.settings.overlayEnabled) return;

      this.overlay.classList.add(CLASSES.open);
      this.overlay.setAttribute('aria-hidden', 'false');
    }

    closeOverlay() {
      if (!this.overlay) return;

      if (!this.isDrawerOpen() && !this.hasOpenMenuItems()) {
        this.overlay.classList.remove(CLASSES.open);
        this.overlay.setAttribute('aria-hidden', 'true');
      }
    }

    /* --------------------------------------------------------------------------
       Focus helpers
       -------------------------------------------------------------------------- */

    trapDrawerFocus(event) {
      if (!this.drawer) return;

      const focusable = this.getFocusableElements(this.drawer);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusableElements(container) {
      if (!container) return [];

      return Array.from(
        container.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
      ).filter((element) => {
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
    }

    isDesktop() {
      return this.desktopMedia.matches;
    }

    isDrawerOpen() {
      return Boolean(this.drawer && this.drawer.classList.contains(CLASSES.open));
    }
  }

  function initITFHeaders() {
    const headers = Array.from(document.querySelectorAll(SELECTORS.header));

    headers.forEach((header) => {
      if (header.dataset.itfHeaderInitialized === 'true') return;

      header.dataset.itfHeaderInitialized = 'true';
      new ITFHeader(header);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initITFHeaders);
  } else {
    initITFHeaders();
  }

  document.addEventListener('shopify:section:load', initITFHeaders);
})();